import React from "react";
import { Box, Typography, IconButton, Tooltip, Divider } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import DashboardCard from "../components/DashboardCard";
import ApiLoading from "../components/ApiLoading";
import { useIaCData } from "./useIaCData";
import { useOrganizations } from "../../../components/Common/HelperComponents/GetDataFromStore";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import NextPlanOutlined from "@mui/icons-material/NextPlanOutlined";

const IaCInsight = () => {
  const history = useHistory();
  const organizations = useOrganizations();
  const orgCode = organizations?.[0]?.orgcode;

  const { data, loading, error } = useIaCData(orgCode);
  console.log("🚀 ~ IaCInsight ~ data:", data);

  const totalTemplates = data?.totalTemplates || 0;
  const limit = data?.limit || 0;
  const passedTemplates = data?.passed || 0;
  const failedTemplates = data?.failed || 0;
  const scriptTypeCounts = data?.scriptTypeCounts || {};

  const vulnerabilities = [
    { name: "Critical", count: data?.critical || 0 },
    { name: "High", count: data?.high || 0 },
    { name: "Medium", count: data?.medium || 0 },
    { name: "Low", count: data?.low || 0 },
    { name: "Info", count: data?.info || 0 },
  ];

  const getColor = (severity) => {
    switch (severity) {
      case "Low":
        return "#4caf50"; // green
      case "Critical":
        return "#7a0709"; // dark red
      case "Medium":
        return "#ff9800"; // orange
      case "High":
        return "#f44336"; // red
      case "Info":
        return "#2196f3"; // blue
      default:
        return "#757575"; // grey
    }
  };

  const getScriptTypeColor = (templateType) => {
    const colorMap = {
      terraform: "#623CE4",
      cloudformation: "#FF9900",
      ansible: "#EE0000",
      kubernetes: "#326CE5",
      helm: "#0F1689",
      docker: "#2496ED",
      pulumi: "#402090",
      bicep: "#5195EA",
      sam: "#FF9900",
      serverless: "#FD5750",
      openai: "#10A37F",
      openTofu: "#4B4B4B",
      grpc: "#4285F4",
      cicd: "#F7B500",
      crossplane: "#2E2E3A",
      "docker-compose": "#2496ED",
      azure_blurprints: "#0078D4",
      "azure-resource-manager": "#0078D4",
      "google-deployment-manager": "#4285F4",
      knative: "#3A4CA8",
      aws: "#FF9900"
    };
    return colorMap[templateType.toLowerCase()] || "#BDBDBD";
  };

  const handleGoTo = () => {
    console.log("Go-to action clicked");
    history.push("/iacmanagement/dashboard");
  };

  // Script Type Donut Chart Component
  const ScriptTypeChart = ({ scriptTypeCounts }) => {
    const scriptTypes = Object.entries(scriptTypeCounts);
    const totalScripts = Object.values(scriptTypeCounts).reduce(
      (sum, count) => sum + count,
      0
    );

    if (totalScripts === 0 || scriptTypes.length === 0) return null;

    // Sort by count (descending) and limit to top 8 for chart display
    const sortedScriptTypes = scriptTypes
      .sort(([, a], [, b]) => b - a)
      .slice(0, 8);

    // Donut chart dimensions
    const size = 45; // Match the height of the "2/300" section
    const radius = size / 2;
    const innerRadius = radius * 0.6; // Donut hole
    const strokeWidth = radius - innerRadius;
    const centerX = size / 2;
    const centerY = size / 2;

    // Calculate angles for each segment
    let currentAngle = -90; // Start from top
    const segments = sortedScriptTypes.map(([type, count]) => {
      const percentage = count / totalScripts;
      const angle = percentage * 360;
      const startAngle = currentAngle;
      currentAngle += angle;

      return {
        type,
        count,
        percentage,
        startAngle,
        endAngle: currentAngle,
        angle,
      };
    });

    // Helper function to create arc path
    const createArcPath = (startAngle, endAngle, outerRadius, innerRadius) => {
      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;

      const x1 = centerX + outerRadius * Math.cos(startRad);
      const y1 = centerY + outerRadius * Math.sin(startRad);
      const x2 = centerX + outerRadius * Math.cos(endRad);
      const y2 = centerY + outerRadius * Math.sin(endRad);

      const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

      const outerArc = `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;
      const innerArc = `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${
        centerX + innerRadius * Math.cos(startRad)
      } ${centerY + innerRadius * Math.sin(startRad)}`;

      return `M ${x1} ${y1} ${outerArc} L ${
        centerX + innerRadius * Math.cos(endRad)
      } ${centerY + innerRadius * Math.sin(endRad)} ${innerArc} Z`;
    };

    const tooltipContent = (
      <Box sx={{ p: 1, minWidth: 150 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
        >
          Template Types
        </Typography>
        {scriptTypes.map(([type, count], index) => (
          <Box
            key={type}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flex: 1, mr: 2 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: getScriptTypeColor(type),
                  mr: 1,
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textTransform: "capitalize" }}
              >
                {type === "serverlessfw" ? "Serverless" : type}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                minWidth: "fit-content",
              }}
            >
              {count}
            </Typography>
          </Box>
        ))}
        <Divider sx={{ my: 0.5 }} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Total
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: "text.primary" }}
          >
            {totalScripts}
          </Typography>
        </Box>
      </Box>
    );

    return (
      <Tooltip
        title={tooltipContent}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: "background.paper",
              color: "text.primary",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: 3,
              "& .MuiTooltip-arrow": {
                color: "background.paper",
                "&::before": {
                  border: "1px solid",
                  borderColor: "divider",
                },
              },
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, cursor: 'pointer' }}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {/* Background circle for depth */}
            <circle
              cx={centerX}
              cy={centerY}
              r={radius}
              fill="rgba(0,0,0,0.03)"
            />
            {/* Donut segments */}
            {segments.length === 1 ? (
              // Special case: single segment (full donut)
              <g>
                <circle
                  cx={centerX}
                  cy={centerY}
                  r={radius}
                  fill={getScriptTypeColor(segments[0].type)}
                  opacity="0.92"
                />
                <circle
                  cx={centerX}
                  cy={centerY}
                  r={innerRadius}
                  fill="white"
                />
              </g>
            ) : (
              segments.map((segment, index) => (
                <g key={segment.type}>
                  {/* Shadow for depth */}
                  <path
                    d={createArcPath(segment.startAngle, segment.endAngle, radius + 1, innerRadius + 1)}
                    fill="rgba(0,0,0,0.08)"
                  />
                  {/* Main segment */}
                  <path
                    d={createArcPath(segment.startAngle, segment.endAngle, radius, innerRadius)}
                    fill={getScriptTypeColor(segment.type)}
                    opacity="0.92"
                  />
                  {/* Highlight overlay */}
                  <path
                    d={createArcPath(segment.startAngle, segment.endAngle, radius, innerRadius)}
                    fill="rgba(255,255,255,0.15)"
                    opacity="0.6"
                  />
                </g>
              ))
            )}
          </svg>
        </Box>
      </Tooltip>
    );
  };

  return (
    <DashboardCard
      title="IaC Scanning"
      actions={[
        {
          label: "go-to",
          icon: (
            <Tooltip title="Open Detailed View">
              <IconButton
                onClick={handleGoTo}
                size="small"
                sx={{
                  p: 0,
                  color: "text.secondary",
                  transform: "rotate(-45deg)",
                }}
              >
                <NextPlanOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
        },
      ]}
      sx={{
        "& .MuiBox-root:last-child": {
          overflowY: "visible",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        },
      }}
    >
      {loading ? (
        <ApiLoading variant="card" height={150} width="100%" />
      ) : (
        <>
          {/* Total Templates Scanned */}
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box display="flex" alignItems="center">
              <Box
                sx={{
                  width: "4px",
                  height: "45px",
                  backgroundColor: "#0073bb",
                  marginRight: "8px",
                  borderRadius: "2px",
                }}
              />
              <Box>
                <Tooltip
                  title={`${totalTemplates} out of ${limit} templates scanned`}
                  arrow
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", fontSize: "1rem" }}
                  >
                    {totalTemplates} / {limit}
                  </Typography>
                </Tooltip>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontSize: "0.65rem" }}
                >
                  Total templates scanned this month
                </Typography>
              </Box>
            </Box>
            {/* Template Types Chart */}
            {Object.keys(scriptTypeCounts).length > 0 && (
              <ScriptTypeChart scriptTypeCounts={scriptTypeCounts} />
            )}
          </Box>

          {/* Compliance Summary */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ paddingLeft: "8px", mb: 1 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0 }}>
              Scan results
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",
              flexGrow: 1,
              pr: 1,
              mb: 2,
              display: "flex",
              flexWrap: "wrap",
              "& > div": {
                flexBasis: "50%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingLeft: "8px",
                paddingRight: "8px",
                marginTop: "0.5rem",
              },
            }}
          >
            <Box>
              <Box display="flex" alignItems="center">
                <FiberManualRecordIcon
                  sx={{ color: "#4caf50", fontSize: "10px", mr: 1 }}
                />
                <Typography variant="body2">Passed</Typography>
              </Box>
              <Typography variant="body2" sx={{ textAlign: "right" }}>
                {passedTemplates}
              </Typography>
            </Box>
            <Box>
              <Box display="flex" alignItems="center">
                <FiberManualRecordIcon
                  sx={{ color: "#f44336", fontSize: "10px", mr: 1 }}
                />
                <Typography variant="body2">Failed</Typography>
              </Box>
              <Typography variant="body2" sx={{ textAlign: "right" }}>
                {failedTemplates}
              </Typography>
            </Box>
          </Box>

          {/* Vulnerability Severity Overview */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ paddingLeft: "8px", mb: 1 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0 }}>
              Vulnerabilities Identified
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",
              flexGrow: 1,
              pr: 1,
              mb: 1,
              display: "flex",
              flexWrap: "wrap",
              "& > div": {
                flexBasis: "50%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingLeft: "8px",
                paddingRight: "8px",
                marginTop: "0.5rem",
              },
            }}
          >
            {vulnerabilities.map((vuln, index) => (
              <Box key={index}>
                <Box display="flex" alignItems="center">
                  <FiberManualRecordIcon
                    sx={{ color: getColor(vuln.name), fontSize: "10px", mr: 1 }}
                  />
                  <Typography variant="body2">{vuln.name}</Typography>
                </Box>
                <Typography variant="body2" sx={{ textAlign: "right" }}>
                  {vuln.count}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </DashboardCard>
  );
};

export default IaCInsight;
